import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ColumnDef, DataTableComponent } from '../../../shared/components/data-table/data-table';
import { EditDataElementModalComponent } from './edit-data-element-modal';
import { CreateDataElementModalComponent } from './create-data-element-modal';
import { ConfiguredDataElementListItem } from '../../../shared/models/program.models';
import { DataElementService } from '../../../core/services/data-element.service';

@Component({
  selector: 'app-data-elements',
  imports: [DataTableComponent, EditDataElementModalComponent, CreateDataElementModalComponent],
  templateUrl: './data-elements.html',
})
export class DataElementsComponent implements OnInit {
  private dataElementService = inject(DataElementService);
  private destroyRef = inject(DestroyRef);

  protected dataElements = signal<ConfiguredDataElementListItem[]>([]);
  protected total = signal(0);
  protected pageIndex = signal(0);
  protected loading = signal(false);
  protected readonly pageSize = 10;
  protected searchQuery = signal('');
  protected editingElement = signal<ConfiguredDataElementListItem | null>(null);
  protected showCreate = signal(false);

  private dataTypeTpl =
    viewChild.required<TemplateRef<{ $implicit: ConfiguredDataElementListItem }>>('dataTypeTpl');

  protected readonly columns = computed<ColumnDef<ConfiguredDataElementListItem>[]>(() => [
    {
      key: 'elementName',
      header: 'Element Name',
      cell: (r) => r.elementName,
      cellClass: 'font-medium text-navy-900',
    },
    { key: 'dataType', header: 'Data Type', cellTemplate: this.dataTypeTpl() },
    {
      key: 'assignments',
      header: 'Program Assignments',
      cell: (r) => String(r.assignments),
      cellClass: 'text-slate-500',
    },
  ]);

  ngOnInit(): void {
    this.load();
  }

  protected load(): void {
    this.loading.set(true);
    this.dataElementService
      .getDataElementsPaged({
        pageSize: this.pageSize,
        pageIndex: this.pageIndex(),
        filter: this.searchQuery(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.dataElements.set(res.configuredDataElements ?? []);
          this.total.set(res.total);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  protected onPageChange(page: number): void {
    this.pageIndex.set(page - 1);
    this.load();
  }

  protected onRowAction(element: ConfiguredDataElementListItem): void {
    this.editingElement.set(element);
  }

  protected onSearchInput(event: Event): void {
    this.searchQuery.set((event.target as HTMLInputElement).value);
    this.pageIndex.set(0);
    this.load();
  }
}
